import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Users, FileText, Download, Pencil, CalendarDays, Clock, List, UserCheck, UserX, CheckCircle2, Search } from 'lucide-react';
import { apprenantService } from '../../services/api';
import AddApprenantDialog from '../../components/AddApprenantDialog';
import ApprenantCard from '../../components/ApprenantCard';
import FreinsDetaillesModule from '../../components/FreinsDetaillesModule';
import DocumentUploader from '../../components/DocumentUploader';
import AddActionDialog from '../../components/AddActionDialog';
import EditActionDialog from '../../components/EditActionDialog';
import EntreeDispositifCard from '../../components/EntreeDispositifCard';
import SynthesesBeneficiaire from '../../components/SynthesesBeneficiaire';

const AdminBeneficiairesView = ({
  beneficiaires,
  selectedBeneficiaire,
  setSelectedBeneficiaire,
  users,
  actions,
  beneficiaireStats,
  beneficiaireAteliers = [],
  editingAction,
  setEditingAction,
  onRefresh,
  onLoadBeneficiaireData,
  onGeneratePDF,
  onShowSyntheseBenefDialog
}) => {
  const referents = users.filter(u => u.role === 'referent');

  // ── Statut de réception (1ère signature d'émargement) ───────────────────────
  // « Déjà reçu » = au moins une signature ; « Jamais reçu » = aucune signature.
  // Calculé côté backend (lecture seule) via /apprenants/statut-reception.
  const [receptionStatus, setReceptionStatus] = useState({});
  const [filtreReception, setFiltreReception] = useState('tous'); // tous | recus | jamais
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    let actif = true;
    apprenantService.getReceptionStatus()
      .then(res => { if (actif) setReceptionStatus(res.data || {}); })
      .catch(() => { /* silencieux : la liste reste affichée sans le statut */ });
    return () => { actif = false; };
  }, [beneficiaires.length]);

  const estRecu = (benef) => !!receptionStatus[benef.id]?.recu;
  const datePremiereSignature = (benef) => receptionStatus[benef.id]?.date_premiere_signature || null;

  // Filtre texte + filtre réception
  const term = recherche.trim().toLowerCase();
  const matchTexte = (b) => term === '' ||
    `${b.prenom} ${b.nom}`.toLowerCase().includes(term) ||
    (b.zone_geographique || '').toLowerCase().includes(term);

  const beneficiairesFiltres = beneficiaires.filter(matchTexte);
  const recus = beneficiairesFiltres.filter(estRecu);
  const jamais = beneficiairesFiltres.filter(b => !estRecu(b));

  const nbRecusTotal = beneficiaires.filter(estRecu).length;
  const nbJamaisTotal = beneficiaires.length - nbRecusTotal;

  // RÈGLE MÉTIER : seules les présences signées (intervenant + bénéficiaire,
  // ou animateur + participant pour les ateliers) sont décomptées du quota 60h.
  // Le backend (/actions/stats/{apprenant_id}) applique déjà cette règle.
  // On lit donc directement les valeurs renvoyées par l'API plutôt que de
  // recalculer côté front (sinon on double les heures ou on ignore la signature).
  const quotaHeures = beneficiaireStats?.quota_heures || 60;
  const actionsHours = beneficiaireStats?.heures_actions || 0;
  const ateliersTime = beneficiaireStats?.heures_ateliers || 0;
  const totalHours = beneficiaireStats?.total_hours || 0;
  const heuresRestantes = beneficiaireStats?.heures_restantes ?? Math.max(quotaHeures - totalHours, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Liste des bénéficiaires */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Bénéficiaires
                <span className="text-sm font-normal text-slate-500 ml-2">({beneficiaires.length})</span>
              </CardTitle>
              <AddApprenantDialog 
                onSuccess={onRefresh} 
                referents={referents}
              />
            </div>
            <Button
              onClick={onShowSyntheseBenefDialog}
              variant="outline"
              size="sm"
              className="mt-3 w-full rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-400"
              data-testid="btn-synthese-beneficiaires"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger synthèse PDF
            </Button>
            <Button
              onClick={() => {
                const api = process.env.REACT_APP_BACKEND_URL || '/api';
                const token = localStorage.getItem('token') || '';
                const url = `${api}/reporting/liste-beneficiaires/pdf`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.ok ? r.blob() : Promise.reject('Erreur'))
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `liste_beneficiaires_${new Date().toISOString().slice(0,10)}.pdf`;
                    a.click();
                  })
                  .catch(() => alert('Erreur lors du téléchargement'));
              }}
              variant="outline"
              size="sm"
              className="mt-2 w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-400"
              data-testid="btn-liste-pdf"
            >
              <List className="w-4 h-4 mr-2" />
              Exporter la liste en PDF
            </Button>
          </CardHeader>
          <CardContent>
            {/* Recherche */}
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={recherche}
                onChange={e => setRecherche(e.target.value)}
                placeholder="Rechercher un bénéficiaire…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="benef-search"
              />
            </div>

            {/* Filtres rapides : Tous / Déjà reçus / Jamais reçus */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'tous', label: 'Tous', count: beneficiaires.length },
                { key: 'recus', label: 'Déjà reçus', count: nbRecusTotal },
                { key: 'jamais', label: 'Jamais reçus', count: nbJamaisTotal },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFiltreReception(f.key)}
                  data-testid={`filtre-${f.key}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    filtreReception === f.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                    filtreReception === f.key ? 'bg-blue-500' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {f.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1">
              {/* Groupe : Déjà reçus */}
              {(filtreReception === 'tous' || filtreReception === 'recus') && recus.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <UserCheck className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                      Déjà reçus ({recus.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {recus.map(benef => (
                      <BeneficiaireItem
                        key={benef.id}
                        benef={benef}
                        referent={users.find(u => u.id === benef.referent_id)}
                        recu
                        datePremiere={datePremiereSignature(benef)}
                        selected={selectedBeneficiaire?.id === benef.id}
                        onSelect={() => setSelectedBeneficiaire(benef)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Groupe : Jamais reçus */}
              {(filtreReception === 'tous' || filtreReception === 'jamais') && jamais.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <UserX className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Jamais reçus ({jamais.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {jamais.map(benef => (
                      <BeneficiaireItem
                        key={benef.id}
                        benef={benef}
                        referent={users.find(u => u.id === benef.referent_id)}
                        recu={false}
                        datePremiere={null}
                        selected={selectedBeneficiaire?.id === benef.id}
                        onSelect={() => setSelectedBeneficiaire(benef)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {beneficiairesFiltres.length === 0 && (
                <p className="text-center text-sm text-slate-400 py-8">
                  Aucun bénéficiaire ne correspond à ces critères.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Détails du bénéficiaire sélectionné */}
      <div className="lg:col-span-2">
        {selectedBeneficiaire ? (
          <div className="space-y-6">
            <ApprenantCard 
              bénéficiaire={selectedBeneficiaire} 
              onUpdate={onRefresh} 
              onDelete={() => {
                setSelectedBeneficiaire(null);
                onRefresh();
              }}
              userRole="admin"
            />
            
            <EntreeDispositifCard
              bénéficiaireId={selectedBeneficiaire.id}
              bénéficiaireEmail={selectedBeneficiaire.email}
            />

            <FreinsDetaillesModule bénéficiaireId={selectedBeneficiaire.id} />

            <DocumentUploader bénéficiaireId={selectedBeneficiaire.id} />
            
            {/* Section Actions, RDV & Ateliers */}
            <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Actions, RDV & Ateliers
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={onGeneratePDF}
                    data-testid="generate-pdf-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Générer PDF
                  </Button>
                  <AddActionDialog 
                    bénéficiaireId={selectedBeneficiaire.id} 
                    onSuccess={() => onLoadBeneficiaireData(selectedBeneficiaire.id)} 
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* Compteur heures — basé UNIQUEMENT sur les présences signées */}
                <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div className="text-lg font-bold text-blue-900">
                      Total : {totalHours} h / {quotaHeures} h
                    </div>
                    <div className="text-sm font-semibold text-blue-700">
                      Reste : {heuresRestantes} h
                    </div>
                  </div>
                  <div className="text-sm text-blue-700 flex flex-wrap gap-3 mt-1">
                    <span>{beneficiaireStats?.action_count || 0} actions/RDV signés ({actionsHours} h)</span>
                    <span className="text-emerald-700">+ {beneficiaireStats?.atelier_count || 0} ateliers signés ({ateliersTime} h)</span>
                  </div>
                  <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (totalHours / quotaHeures) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    Seules les présences signées (intervenant + bénéficiaire) sont décomptées.
                  </p>
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" />
                    Actions et RDV ({actions.length})
                  </h4>
                  {actions.length === 0 ? (
                    <p className="text-slate-500 text-center py-2 text-sm">Aucune action enregistrée</p>
                  ) : (
                    actions.map((action) => (
                      <ActionItem 
                        key={action.id} 
                        action={action} 
                        onEdit={() => setEditingAction(action)}
                      />
                    ))
                  )}
                </div>

                {/* Ateliers */}
                <div className="mt-6 space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <CalendarDays className="w-4 h-4" />
                    Ateliers ({beneficiaireAteliers.length})
                  </h4>
                  {beneficiaireAteliers.length === 0 ? (
                    <p className="text-slate-500 text-center py-2 text-sm">Aucun atelier</p>
                  ) : (
                    beneficiaireAteliers.map((atelier) => (
                      <AtelierItem 
                        key={atelier.id} 
                        atelier={atelier} 
                        beneficiaireId={selectedBeneficiaire.id}
                      />
                    ))
                  )}
                </div>
                
                <EditActionDialog
                  action={editingAction}
                  open={!!editingAction}
                  onOpenChange={(open) => !open && setEditingAction(null)}
                  onSuccess={() => onLoadBeneficiaireData(selectedBeneficiaire.id)}
                />
              </CardContent>
            </Card>

            {/* Historique des synthèses du bénéficiaire */}
            <div className="mt-6">
              <SynthesesBeneficiaire apprenantId={selectedBeneficiaire.id} />
            </div>
          </div>
        ) : (
          <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-500 text-center">
                Sélectionnez un bénéficiaire pour voir ses détails
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Sous-composant : ligne d'un bénéficiaire dans la liste, avec badges
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const BeneficiaireItem = ({ benef, referent, recu, datePremiere, selected, onSelect }) => {
  const consentement = benef.entree_dispositif_validee === true;
  return (
    <div
      data-testid={`beneficiaire-item-${benef.id}`}
      onClick={onSelect}
      className={`p-4 rounded-xl cursor-pointer transition-all ${
        selected
          ? 'bg-blue-50 border-2 border-blue-200'
          : 'bg-slate-50 border-2 border-transparent hover:border-blue-100'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-slate-900 truncate">
            {benef.prenom} {benef.nom}
          </div>
          <div className="text-sm text-slate-500 truncate">
            {benef.zone_geographique} • {referent ? `${referent.prenom} ${referent.nom}` : 'Sans référent'}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {recu ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
            <UserCheck className="w-3 h-3" />
            Reçu{datePremiere ? ` · ${fmtDate(datePremiere)}` : ''}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">
            <UserX className="w-3 h-3" />
            Jamais reçu
          </span>
        )}
        {consentement && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
            <CheckCircle2 className="w-3 h-3" />
            Consentement
          </span>
        )}
      </div>
    </div>
  );
};

// Sous-composant ActionItem
const ActionItem = ({ action, onEdit }) => {
  return (
    <div
      data-testid={`action-item-${action.id}`}
      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="font-bold text-slate-900">{action.titre}</div>
          <div className="text-sm text-slate-600 mt-1">
            {action.date} • {action.heure_debut} - {action.heure_fin}
          </div>
          {action.notes && (
            <div className="text-sm text-slate-500 mt-2">{action.notes}</div>
          )}
        </div>
        <div className="flex items-start gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="rounded-xl"
            data-testid={`edit-action-${action.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <div className="flex flex-col items-end gap-1">
            <div className="px-3 py-1 rounded-full text-sm font-bold bg-amber-100 text-amber-700">
              {action.duree_minutes} min
            </div>
            <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              action.statut_presence === 'present'
                ? 'bg-emerald-100 text-emerald-700'
                : action.statut_presence === 'absent_justifie'
                  ? 'bg-amber-100 text-amber-700'
                  : action.statut_presence === 'non_defini' || !action.statut_presence
                    ? 'bg-slate-100 text-slate-600'
                    : 'bg-red-100 text-red-700'
            }`}>
              {action.statut_presence === 'present' ? 'Présent' :
               action.statut_presence === 'absent_justifie' ? 'Abs. justifiée' :
               action.statut_presence === 'non_defini' || !action.statut_presence ? 'Non défini' : 'Absent'}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-2 flex-wrap">
        {action.synthese ? (
          <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 font-bold">
            ✍ Synthèse rédigée
          </span>
        ) : (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-bold border border-amber-200">
            Synthèse à compléter
          </span>
        )}
        {action.signature_intervenant && (
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
            Signé intervenant
          </span>
        )}
        {action.signature_apprenant && (
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
            Signé bénéficiaire
          </span>
        )}
        {action.justificatif_id && (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-bold">
            Justificatif fourni
          </span>
        )}
      </div>
    </div>
  );
};

// Sous-composant AtelierItem
const AtelierItem = ({ atelier, beneficiaireId }) => {
  const participant = atelier.participants?.find(
    p => p.id === beneficiaireId || p.apprenant_id === beneficiaireId
  );
  const isPresent = participant?.statut_presence === 'present' || participant?.present === true;
  const isNonDefini = !participant?.statut_presence || participant?.statut_presence === 'non_defini';

  // Calculer la durée
  let dureeMinutes = 0;
  if (atelier.heure_debut && atelier.heure_fin) {
    const [hDebut, mDebut] = atelier.heure_debut.split(':').map(Number);
    const [hFin, mFin] = atelier.heure_fin.split(':').map(Number);
    dureeMinutes = (hFin * 60 + mFin) - (hDebut * 60 + mDebut);
  }

  return (
    <div
      data-testid={`atelier-item-${atelier.id}`}
      className="p-4 bg-emerald-50 rounded-xl border border-emerald-200"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Atelier</Badge>
            <span className="font-bold text-slate-900">{atelier.titre}</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {atelier.date} • {atelier.heure_debut} - {atelier.heure_fin}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Type : {atelier.type_atelier || 'Non spécifié'}
          </div>
          {participant?.signature && (
            <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
              ✓ Signé
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="px-3 py-1 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700">
            {dureeMinutes} min
          </div>
          <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${
            isPresent
              ? 'bg-emerald-100 text-emerald-700'
              : isNonDefini
                ? 'bg-slate-100 text-slate-600'
                : 'bg-red-100 text-red-700'
          }`}>
            {isPresent ? 'Présent' : isNonDefini ? 'Non défini' : 'Absent'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBeneficiairesView;
